import { useState, type ReactNode } from 'react';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import { useTranslation } from 'react-i18next';
import { useTenantPresentation } from '@eis/core';

/** The sessionStorage key that says this browser session has seen the notice for a tenant. */
export function demoNoticeKey(tenantId: string): string {
  return `eis.demoNotice.${tenantId}`;
}

/**
 * Storage can be absent or throw (a private window, blocked site data). The notice is a courtesy,
 * not a gate: without storage it opens once per page load instead of once per session.
 */
function alreadySeen(tenantId: string): boolean {
  try {
    return window.sessionStorage.getItem(demoNoticeKey(tenantId)) !== null;
  } catch {
    return false;
  }
}

function markSeen(tenantId: string): void {
  try {
    window.sessionStorage.setItem(demoNoticeKey(tenantId), 'seen');
  } catch {
    // See alreadySeen: without storage the notice simply opens again on the next load.
  }
}

/**
 * The demo notice (SDD-022 D-8): a tenant the ENVIRONMENT marks with `notice: true`
 * (`chrome.demoTenants`, T-53) sees it once per browser session after sign-in. It says what the
 * environment is — a fully working pre-beta on the real platform with demo data, no mock-up, the
 * release planned for the coming weeks, data that may be reset — and has one action.
 *
 * Chrome, not a pattern: one mount point in the shell, nothing in a module would ever render it.
 * No permission: every user of such a tenant sees it (SDD-022 §10).
 */
export function DemoNoticeDialog(): ReactNode {
  const { t } = useTranslation('shell');
  const { tenantId, notice } = useTenantPresentation();
  // Decided once per mount for the tenant signed in; dismissing records it for the session.
  const [dismissedFor, setDismissedFor] = useState<string | null>(null);

  const open = tenantId !== null && notice && dismissedFor !== tenantId && !alreadySeen(tenantId);

  const dismiss = (): void => {
    if (tenantId === null) return;
    markSeen(tenantId);
    setDismissedFor(tenantId);
  };

  return (
    <Dialog
      open={open}
      onClose={dismiss}
      aria-labelledby="demo-notice-title"
      aria-describedby="demo-notice-body"
      data-testid="demo-notice"
      maxWidth="sm"
    >
      <DialogTitle id="demo-notice-title">{t('demoNotice.title')}</DialogTitle>
      <DialogContent>
        <DialogContentText id="demo-notice-body" sx={{ mb: 1.5 }}>
          {t('demoNotice.body')}
        </DialogContentText>
        <DialogContentText>{t('demoNotice.release')}</DialogContentText>
      </DialogContent>
      <DialogActions>
        <Button
          variant="contained"
          size="small"
          onClick={dismiss}
          data-testid="demo-notice-continue"
        >
          {t('demoNotice.continue')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
